import { useCallback, useEffect, useRef, useState } from 'react';

import {
	deriveSagaView,
	isAgentTerminal,
	isSagaTerminal,
	nextPollDelay,
	POLL_CEILING_MS,
	withJitter,
} from '../components/pages/saga-status/engine';
import type { ISagaRow, ISagaView, SagaLevel } from '../interface/saga-status';
import { getSagaState, triggerAiFetch } from '../lib/saga-status-api';

export type SagaStatusPhase = 'idle' | 'checking' | 'ready' | 'offline' | 'unreachable';

export interface IUseSagaStatus {
	phase: SagaStatusPhase;
	view: ISagaView;
	row: ISagaRow | null;
	/** True while polling is still expected to produce a change. */
	isWatching: boolean;
	refresh: () => void;
	/** Queue an AI re-extraction. Resolves once queued — NOT once anything has happened. */
	queueAiRetry: () => void;
	retryState: 'idle' | 'queueing' | 'queued' | 'moved' | 'unchanged' | 'error';
}

export interface IUseSagaStatusArgs {
	level: SagaLevel;
	electionId: string | number | undefined;
	code: string | undefined;
	/**
	 * Must be false whenever the surface is not visible. The upload-result panel stays mounted
	 * behind every other panel, so a hook running there would poll forever in the background.
	 */
	enabled: boolean;
}

/** How long to watch for movement after queueing a retry before calling it unchanged. */
const WATCH_WINDOW_MS = 90_000;

/**
 * Did the pipeline actually act? The trigger endpoint answers 202 whether or not the message
 * was acted on, so movement has to be observed on the row itself.
 */
export const hasMoved = (before: ISagaRow, after: ISagaRow): boolean =>
	after.updatedAt !== before.updatedAt ||
	after.version > before.version ||
	after.currentState !== before.currentState ||
	after.isAiExtractionCompleted !== before.isAiExtractionCompleted ||
	after.isAiExtractionFailed !== before.isAiExtractionFailed;

/**
 * Poll one entity's saga row and derive what the agent is told.
 *
 * Cadence backs off (see `nextPollDelay`) with jitter, skips ticks while the tab is hidden or
 * the device is offline rather than pausing (so the schedule does not drift), and stops once
 * the view is agent-terminal or the ceiling is reached. A failed poll never changes the
 * verdict — last known good wins — and never raises a notification.
 */
export const useSagaStatus = ({ level, electionId, code, enabled }: IUseSagaStatusArgs): IUseSagaStatus => {
	const [row, setRow] = useState<ISagaRow | null>(null);
	const [phase, setPhase] = useState<SagaStatusPhase>('idle');
	const [retryState, setRetryState] = useState<IUseSagaStatus['retryState']>('idle');

	// Guards against a slow response from a previous entity overwriting a newer one.
	const runIdRef = useRef(0);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const attemptRef = useRef(0);
	const startedAtRef = useRef(0);
	const failuresRef = useRef(0);
	// Snapshot taken when a retry is queued, so movement can be detected rather than assumed.
	const watchRef = useRef<{ until: number; before: ISagaRow } | null>(null);

	const view = deriveSagaView(row, level);
	const settled = isAgentTerminal(view) || isSagaTerminal(row);

	const clearTimer = () => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = null;
	};

	const poll = useCallback(
		async (runId: number) => {
			if (runId !== runIdRef.current) return;

			// Skip rather than pause: the schedule keeps its place, and a backgrounded phone
			// or a dead connection is not a reason to burn an attempt.
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				setPhase('offline');
				return;
			}

			const outcome = await getSagaState({ level, electionId: electionId as string, code: code! });
			if (runId !== runIdRef.current) return;

			if (!outcome.reachable) {
				failuresRef.current += 1;
				// Keep the last known good view; only say so once it is clearly not a blip.
				if (failuresRef.current >= 3) setPhase('unreachable');
				return;
			}

			failuresRef.current = 0;
			setPhase('ready');

			// Resolve the retry watch here, not inside a setState updater — React may invoke an
			// updater twice, and this branch has side effects.
			const watch = watchRef.current;
			if (watch && outcome.row) {
				if (hasMoved(watch.before, outcome.row)) {
					watchRef.current = null;
					setRetryState('moved');
				} else if (Date.now() > watch.until) {
					watchRef.current = null;
					setRetryState('unchanged');
				}
			}

			// A poll that finds no row yet must not wipe a row we already have.
			if (outcome.row) setRow(outcome.row);
		},
		[level, electionId, code]
	);

	// Drives the backoff chain. Rescheduling keys off `settled` so a row that reaches a
	// terminal view stops the loop on the next tick rather than running to the ceiling.
	useEffect(() => {
		if (!enabled || !electionId || !code) {
			clearTimer();
			return;
		}

		runIdRef.current += 1;
		const runId = runIdRef.current;
		attemptRef.current = 0;
		startedAtRef.current = Date.now();
		failuresRef.current = 0;
		setPhase('checking');

		const tick = async () => {
			await poll(runId);
			if (runId !== runIdRef.current) return;
			if (Date.now() - startedAtRef.current > POLL_CEILING_MS) return;

			attemptRef.current += 1;
			timerRef.current = setTimeout(() => void tick(), withJitter(nextPollDelay(attemptRef.current)));
		};

		void tick();

		const onVisible = () => {
			if (document.visibilityState === 'visible') void poll(runId);
		};
		const onOnline = () => void poll(runId);

		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('online', onOnline);

		return () => {
			runIdRef.current += 1;
			clearTimer();
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('online', onOnline);
		};
	}, [enabled, electionId, code, poll]);

	// Stop the chain once there is nothing left to observe, but keep the last view on screen.
	useEffect(() => {
		if (settled && !watchRef.current) clearTimer();
	}, [settled]);

	const refresh = useCallback(() => {
		attemptRef.current = 0;
		startedAtRef.current = Date.now();
		void poll(runIdRef.current);
	}, [poll]);

	const queueAiRetry = useCallback(async () => {
		if (!row || !electionId || !code) return;

		setRetryState('queueing');
		const outcome = await triggerAiFetch({ level, electionId, code });

		if (!outcome.queued) {
			setRetryState('error');
			return;
		}

		// A 202 only means the message was accepted for delivery. Whether the pipeline acts on
		// it is observed by re-reading the row, never inferred from this response.
		watchRef.current = { until: Date.now() + WATCH_WINDOW_MS, before: row };
		setRetryState('queued');
		attemptRef.current = 0;
		startedAtRef.current = Date.now();
		void poll(runIdRef.current);
	}, [row, electionId, code, level, poll]);

	return {
		phase,
		view,
		row,
		isWatching: !settled || !!watchRef.current,
		refresh,
		queueAiRetry: () => void queueAiRetry(),
		retryState,
	};
};
